import { useState, useRef } from "react";
import { Camera, Upload, X, Loader2, Maximize2, AlertCircle } from "lucide-react";
import type { 
  PpmChecklistPhoto, 
  PpmChecklistPhotoTaskKey, 
  PpmChecklistPhotoStage 
} from "@workspace/api-client-react";
import { useUploadPpmPhoto, useDeletePpmPhoto } from "@/hooks/use-ppm-photos";
import { usePpmPhotosContext } from "@/hooks/use-ppm-photos-context";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface PpmPhotoSlotProps {
  inspectionId: number;
  taskKey: PpmChecklistPhotoTaskKey;
  stage: PpmChecklistPhotoStage;
  photo?: PpmChecklistPhoto;
  isReadOnly: boolean;
  taskLabel: string;
}

export function PpmPhotoSlot({ inspectionId, taskKey, stage, photo, isReadOnly, taskLabel }: PpmPhotoSlotProps) {
  const upload = useUploadPpmPhoto();
  const deletePhoto = useDeletePpmPhoto();
  const { addPending, removePending } = usePpmPhotosContext();
  
  const [error, setError] = useState<string | null>(null);
  const [previewEnlarged, setPreviewEnlarged] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly || upload.isPending || deletePhoto.isPending) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate type and size
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      if (file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
        setError("HEIC format is unsupported. Please upload a JPEG, PNG, or WebP.");
      } else {
        setError("Please upload a JPEG, PNG, or WebP image.");
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size exceeds 10MB maximum.");
      return;
    }

    addPending();
    try {
      await upload.mutateAsync({
        id: inspectionId,
        taskKey,
        stage,
        file
      });
    } catch (err: any) {
      setError(err.message || "Failed to upload photo");
    } finally {
      removePending();
      if (e.target) e.target.value = "";
    }
  };

  const handleRemove = async () => {
    if (!photo || isReadOnly || upload.isPending || deletePhoto.isPending) return;
    if (!confirm(`Are you sure you want to remove this ${stage} photo?`)) return;

    setError(null);
    addPending();
    try {
      await deletePhoto.mutateAsync({
        id: inspectionId,
        photoId: photo.id
      });
    } catch (err: any) {
      setError(err.message || "Failed to remove photo");
    } finally {
      removePending();
    }
  };

  const isPending = upload.isPending || deletePhoto.isPending;

  return (
    <div className="flex flex-col gap-2 p-3 bg-slate-50 border border-slate-100 rounded-lg shrink-0 print:p-0 print:border-none print:bg-transparent">
      <div className="flex items-center justify-between print:hidden">
        <span className="text-xs font-semibold text-slate-700 capitalize">{stage}</span>
        {error && (
          <span className="text-[10px] text-red-600 flex items-center gap-1 bg-red-50 px-1.5 py-0.5 rounded" title={error}>
            <AlertCircle className="w-3 h-3" /> Error
          </span>
        )}
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
        aria-label={`Take ${stage} photo for ${taskLabel}`}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
        aria-label={`Upload ${stage} photo for ${taskLabel}`}
      />
      
      {photo ? (
        <div className="relative group rounded-md overflow-hidden bg-slate-200 aspect-[4/3] flex items-center justify-center border border-slate-200 print:aspect-auto print:border-none print:bg-transparent">
          <img 
            src={photo.imageUrl} 
            alt={`${taskLabel} ${stage}`}
            className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-90"
          />
          <div className="absolute inset-0 bg-black/40 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 print:hidden">
            <button 
              type="button"
              aria-label={`Enlarge ${stage} photo for ${taskLabel}`}
              onClick={() => setPreviewEnlarged(true)}
              className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-md backdrop-blur-sm transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            {!isReadOnly && (
              <div className="flex gap-2">
                <button 
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isPending}
                  className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-md backdrop-blur-sm transition-colors disabled:opacity-50"
                  aria-label={`Replace ${stage} photo using camera`}
                >
                  <Camera className="w-4 h-4" />
                </button>
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPending}
                  className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-md backdrop-blur-sm transition-colors disabled:opacity-50"
                  aria-label={`Replace ${stage} photo by uploading file`}
                >
                  <Upload className="w-4 h-4" />
                </button>
                <button 
                  type="button"
                  aria-label={`Remove ${stage} photo for ${taskLabel}`}
                  onClick={handleRemove}
                  disabled={isPending}
                  className="p-1.5 bg-red-500/80 hover:bg-red-600 text-white rounded-md backdrop-blur-sm transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <span className="hidden print:block absolute bottom-1 left-1 bg-white/90 px-1 py-0.5 text-[8px] font-bold uppercase rounded text-slate-800">
            {stage}
          </span>
          {isPending && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center print:hidden">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-[10px] font-medium text-slate-700 mt-1">{deletePhoto.isPending ? 'Removing...' : 'Uploading...'}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center min-h-[80px] border-2 border-dashed border-slate-200 rounded-md bg-white print:hidden">
          {isPending ? (
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-[10px] font-medium text-slate-500 mt-1">Uploading...</span>
            </div>
          ) : isReadOnly ? (
            <span className="text-xs text-slate-400 italic">No {stage} photo</span>
          ) : (
            <div className="flex items-center gap-2">
              <button 
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="p-2 text-slate-500 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                title="Take photo"
                aria-label={`Take ${stage} photo for ${taskLabel}`}
              >
                <Camera className="w-4 h-4" />
              </button>
              <span className="text-slate-300">|</span>
              <button 
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-slate-500 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                title="Upload file"
                aria-label={`Upload ${stage} photo for ${taskLabel}`}
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-[10px] text-red-600 leading-tight mt-1 print:hidden">{error}</div>
      )}

      {/* Enlarge Dialog */}
      <Dialog open={previewEnlarged} onOpenChange={setPreviewEnlarged}>
        <DialogContent className="max-w-3xl p-1 bg-black/95 border-none shadow-2xl">
          <DialogTitle className="sr-only">{stage} Photo - {taskLabel}</DialogTitle>
          <DialogDescription className="sr-only">Full size preview of the photo</DialogDescription>
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            {photo && <img src={photo.imageUrl} alt={`${taskLabel} ${stage}`} className="max-w-full max-h-full object-contain" />}
            <button 
              type="button"
              onClick={() => setPreviewEnlarged(false)}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
